import _ from 'lodash'
import { AxiosResponse } from 'axios'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { getType } from 'typesafe-actions'

import { PaginationConfig } from 'antd/lib/pagination'

import { apiCall } from '../../utils/api'
import { isUserLoggedIn } from '../user/selectors'
import { withLoading } from '../loading'
import { TableActions, ColumnSorting, sortParam } from '../table'
import { Dictionary } from '../../utils/types'
import { selectSorting, selectPagination } from '../table/selectors'
import { getVisibleDialogData } from '../dialog/selectors'
import { DialogType } from '../dialog/types'
import { DialogActions } from '../dialog/actions'
import { downloadExportViaToken } from '../../utils/sagaUtils'

import { OrdersActions } from './actions'
import { OrdersResponse } from './types'
import { getTransactionFilters, getTransactionSearchFilter } from './selectors'

type LoadOrdersType = ReturnType<typeof OrdersActions.loadOrders>

const preprocessFilters = (filters: Dictionary<any>) =>
  _.mapValues(filters, filter => (_.has(filter, 'value') ? _.get(filter, 'value') : filter))

export function* loadOrders(action: LoadOrdersType): any {
  const loggedIn: boolean = yield select(isUserLoggedIn)
  if (!loggedIn) return

  const sorting: Dictionary<ColumnSorting> = yield select(selectSorting, { tableKey: 'transactions' })
  const filter: string = yield select(getTransactionSearchFilter)
  const filters: Dictionary<any> = yield select(getTransactionFilters)
  const preprocessedFilters = preprocessFilters(filters)

  const { data: ordersData }: AxiosResponse<OrdersResponse> = yield call(apiCall, {
    url: '/api/ordering/order/search',
    method: 'post',
    params: {
      page: action.payload.page,
      pageSize: action.payload.pageSize,
      sort: sortParam(action.payload.sorting || sorting),
      search: filter || undefined
    },
    data: {
      ...preprocessedFilters,
      orderIds: preprocessedFilters?.orderIds
        ?.split(',')
        .map((v: string) => v.trim())
        .filter(Boolean)
    }
  })

  yield put(
    OrdersActions.setOrders({
      page: ordersData.meta.pageNumber,
      totalPages: ordersData.meta.pageCount,
      data: ordersData.orders
    })
  )

  if (ordersData.meta.pageNumber === 1) {
    yield put(
      TableActions.setPagination({
        tableKey: 'transactions',
        pagination: {
          current: ordersData.meta.pageNumber,
          pageSize: action.payload.pageSize,
          total: ordersData.meta.pageCount * action.payload.pageSize
        }
      })
    )
  }
}

export function* loadOnPageChange({ payload }: ReturnType<typeof TableActions.setPagination>) {
  if (payload.tableKey !== 'transactions') return

  const { pagination } = payload

  yield put(
    OrdersActions.loadOrders({
      page: pagination.current,
      pageSize: pagination.pageSize
    })
  )
}

export function* loadOnSortChange({ payload }: ReturnType<typeof TableActions.setSorting>) {
  if (payload.tableKey !== 'transactions') return

  const pagination: PaginationConfig = yield select(selectPagination, { tableKey: 'transactions' })

  yield put(
    OrdersActions.loadOrders({
      page: pagination.current || 1,
      pageSize: pagination.pageSize || pagination.defaultPageSize || 10
    })
  )
}

export function* loadOnFilterChange() {
  const pagination: PaginationConfig = yield select(selectPagination, { tableKey: 'transactions' })

  yield put(
    OrdersActions.loadOrders({
      page: 1,
      pageSize: pagination.pageSize || pagination.defaultPageSize || 10
    })
  )
}

export function* exportOrders({ payload }: ReturnType<typeof OrdersActions.exportOrders>) {
  const sorting: Dictionary<ColumnSorting> = yield select(selectSorting, { tableKey: 'transactions' })
  const searchFilter: string = yield select(getTransactionSearchFilter)
  const filters: Dictionary<any> = yield select(getTransactionFilters)
  const preprocessedFilters = preprocessFilters(filters)

  try {
    yield call(apiCall, {
      url: '/api/ordering/export',
      method: 'post',
      params: {
        searchOrder: sortParam(sorting),
        search: searchFilter || undefined,
        page: 1,
        pageSize: 1
      },
      data: {
        name: payload.name,
        filter: {
          ...preprocessedFilters,
          orderIds: preprocessedFilters?.orderIds
            ?.split(',')
            .map((v: string) => v.trim())
            .filter(Boolean)
        },
        columns: _.map(payload.columns, x => ({ id: x.id, name: x.name })),
        domains: payload.domains
      }
    })
  } catch (e) {
    const visibleDialog: DialogType = yield select(getVisibleDialogData)
    if (visibleDialog === 'TransactionsExport')
      yield put(
        DialogActions.updateDialogData({
          dialog: 'TransactionsExport',
          data: {
            customColumns: {},
            isError: true
          }
        })
      )
    else {
      yield put(
        DialogActions.showDialogWithData({
          dialog: 'TransactionsExport',
          data: {
            customColumns: {},
            isError: true
          }
        })
      )
    }
  }
}

export function* downloadExport({ payload }: ReturnType<typeof OrdersActions.downloadExport>) {
  const { exportId } = payload

  yield call(downloadExportViaToken, exportId)
  return
}

/* istanbul ignore next */
export function* ordersSagas() {
  yield all([
    takeLatest(getType(OrdersActions.loadOrders), withLoading('orders', loadOrders)),
    takeLatest(getType(TableActions.changePagination), loadOnPageChange),
    takeLatest(getType(TableActions.setSorting), loadOnSortChange),
    takeLatest(getType(OrdersActions.setFilters), loadOnFilterChange),
    takeLatest(getType(OrdersActions.setSearchFilter), loadOnFilterChange),
    takeLatest(getType(OrdersActions.clearFilter), loadOnFilterChange),
    takeLatest(getType(OrdersActions.exportOrders), exportOrders),
    takeLatest(getType(OrdersActions.downloadExport), downloadExport)
  ])
}
